/**
 * Centralized React Query Configuration
 *
 * This file defines cache timing configurations for React Query based on data characteristics.
 * Use these presets to maintain consistency and ensure appropriate caching behavior.
 *
 * Cache Strategy Guide:
 * - staleTime: How long data is considered "fresh" before React Query will refetch it
 * - gcTime: How long unused data stays in cache before garbage collection
 * - refetchOnWindowFocus: Whether to refetch when user returns to the tab
 * - refetchInterval: How often to periodically refetch data (in milliseconds)
 *
 * @module queryConfig
 */

/**
 * Time constants for cache configuration
 */
const MINUTES = 60 * 1000;

/**
 * Cache configuration for collaborative data where multiple users can make changes
 *
 * This config is designed for data that can be modified by multiple admins/users simultaneously,
 * such as organization policies and member lists. It ensures users see the latest changes
 * both while actively viewing the page and when they return to the tab.
 *
 * Characteristics:
 * - staleTime: 0 (data is immediately stale, allowing background refetches)
 * - refetchOnWindowFocus: true (refetch when user returns to tab to catch other users' changes)
 * - refetchInterval: 30 seconds (periodically refetch while user is viewing the page)
 * - gcTime: 5 minutes (keep in cache for quick navigation back)
 *
 * Use for:
 * - Organization policies (multiple admins can toggle/update)
 * - Any data where concurrent edits by multiple users are expected
 *
 * Not for the organization member roster: it is backed by a rate-limited
 * upstream (Descope) — use {@link cacheConfigForSharedRoster}.
 *
 * Rationale:
 * When multiple admins are working simultaneously, one admin's changes won't be visible
 * to others until they refetch. By setting staleTime to 0, enabling refetchOnWindowFocus,
 * and adding periodic refetching, we ensure users see the latest state both:
 * - While actively viewing the page (refetchInterval)
 * - When returning to the tab (refetchOnWindowFocus)
 * - Immediately after their own changes (mutation invalidation)
 *
 * The 30-second interval balances freshness with server load - frequent enough to catch
 * changes quickly, but not so frequent as to cause unnecessary requests.
 */
export const cacheConfigForCollaborativeData = {
  staleTime: 0,
  gcTime: 5 * MINUTES,
  refetchOnWindowFocus: true,
  refetchInterval: 30 * 1000, // 30 seconds - refetch periodically while viewing
} as const;

/**
 * Cache configuration for the organization member roster (KIR-4887)
 *
 * The members list (and the personal-key members list) is built from the
 * Descope tenant roster, and Descope rate-limits user search to 200 requests
 * per minute for the whole project. Polling it every 30 seconds from every
 * open Users tab helped exhaust that budget (INC-346), so this preset does not
 * poll.
 *
 * Characteristics:
 * - staleTime: 60 seconds (remounts / tab switches within a minute reuse data)
 * - refetchOnWindowFocus: true (returning to the tab still picks up changes)
 * - no refetchInterval (no background polling)
 * - gcTime: 5 minutes
 *
 * The admin's own changes reload the roster right away (see
 * `reloadMemberRoster`): adds and role changes with a plain read, since the
 * backend patches its Redis copy through a change log that every read applies;
 * removals with `fresh=true`, since removing an invitee wipes that copy and a
 * plain read could join a fetch that started before it. Other admins' changes
 * made through Kirin show
 * on the next reload (after `staleTime`); changes made outside Kirin show within
 * the backend's 5-minute roster freshness window.
 */
export const cacheConfigForSharedRoster = {
  staleTime: 1 * MINUTES,
  gcTime: 5 * MINUTES,
  refetchOnWindowFocus: true,
} as const;

/**
 * Cache configuration for user-specific data that only the current user can change
 *
 * This config is for data that belongs to or is controlled by a single user, such as
 * their personal policies, API keys, or organization list. Since only the user can
 * modify it, we don't need aggressive refetching.
 *
 * Characteristics:
 * - staleTime: 5 minutes (data stays fresh for 5 minutes)
 * - gcTime: 10 minutes (keep in cache for navigation)
 * - refetchOnWindowFocus: false (no need to refetch, user controls the data)
 *
 * Use for:
 * - User policies and settings (only user changes them)
 * - Personal API key status (only user generates it)
 * - User's organization list (rarely changes, user-specific)
 * - Historical data like scans/detections (completed, doesn't change)
 *
 * Rationale:
 * Since only the user can modify this data, mutation invalidation handles updates
 * immediately. We don't need to refetch on window focus because no one else can
 * change it. The 5-minute staleTime provides a good balance between freshness and
 * performance.
 */
export const cacheConfigForUserSpecificData = {
  staleTime: 5 * MINUTES,
  gcTime: 10 * MINUTES,
  refetchOnWindowFocus: false,
} as const;

/**
 * Cache configuration for static or rarely-changing reference data
 *
 * This config is for data that changes very infrequently, such as organization
 * details or user profiles. These are typically updated only by admins and change
 * rarely, so we can cache them longer.
 *
 * Characteristics:
 * - staleTime: 10 minutes (data stays fresh for 10 minutes)
 * - gcTime: 15 minutes (keep in cache longer for reference)
 * - refetchOnWindowFocus: false (rarely changes, no need for aggressive refetching)
 *
 * Use for:
 * - Organization details (name, settings - rarely change)
 * - User profile information (name, email - rarely change)
 * - Any reference data that is infrequently updated
 *
 * Rationale:
 * This data changes so infrequently that a 10-minute staleTime is appropriate.
 * When it does change, mutation invalidation or manual refetch handles updates.
 * The longer gcTime helps with navigation patterns where users might return to
 * view organization details multiple times.
 */
export const cacheConfigForStaticReferenceData = {
  staleTime: 10 * MINUTES,
  gcTime: 15 * MINUTES,
  refetchOnWindowFocus: false,
} as const;
